import React, { useState } from 'react';
import axios from '../axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';
import logo from '../assets/images/logo_with_text_lg.png';

// Critical LCP asset loaded from public folder for optimal performance
const clouds = '/assets/images/clouds.webp';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Calculate daily changing hue value for cloud background
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 0);
  const diff = today.getTime() - startOfYear.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  const hue = dayOfYear % 360;

  const backgroundStyle: React.CSSProperties = {
    backgroundColor: 'white',
    position: 'relative',
    overflow: 'hidden',
  };

  const cloudImageStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: `url(${clouds})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    filter: `hue-rotate(${hue}deg) saturate(70%) brightness(100%)`,
    opacity: 1.0,
    zIndex: 0,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post('/api/auth/signup', {
        email,
        password,
        phoneNumber: phoneNumber.trim() || null,
      });
      toast.show(t('auth.signupSuccess') as string, 'success');
      setTimeout(() => navigate('/login'), 600);
    } catch (error: any) {
      toast.show(String(error.response?.data || t('auth.signupFailed')), 'error');
    } finally { setLoading(false); }
  };

  return (
    <div style={backgroundStyle} className="min-h-screen flex items-center justify-center px-4">
      <div style={cloudImageStyle}></div>
      {/* Global overlay above clouds for auth pages */}
      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-[1]"></div>

      <div className="relative z-10 w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <img src={logo} alt="HeartPort logo" className="h-14 md:h-20 w-auto" />
        </div>

        <div>
          <h2 className="text-center text-4xl font-bold text-[#49ABDB]">
            {t('auth.signup')}
          </h2>
        </div>

        <div className="bg-[#CAD4DD] p-8 rounded-lg shadow-lg">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-gray-800"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.phoneOptional')}</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111D4E] mb-1">{t('auth.password')}</label>
                <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-gray-800"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#47ABDB] disabled:opacity-60 text-white py-2 rounded-md font-semibold hover:bg-[#3d8fb8]"
            >
              {loading ? t('common.loading') : t('auth.signup')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
